import { useMemo, useState } from 'react';
import { RoofingCompany } from '../../../mocks/roofingData';

interface WakeCountyChartProps {
  data: RoofingCompany[];
  selectedIndices: number[];
}

const VIEWBOX_W = 720;
const VIEWBOX_H = 480;
const PAD = { top: 24, right: 30, bottom: 60, left: 80 };
const PLOT_W = VIEWBOX_W - PAD.left - PAD.right;
const PLOT_H = VIEWBOX_H - PAD.top - PAD.bottom;

const Q_MIN = 8.0;
const Q_MAX = 10.4;
const C_MIN = 8000;
const C_MAX = 16000;

function scaleX(q: number): number {
  return PAD.left + ((q - Q_MIN) / (Q_MAX - Q_MIN)) * PLOT_W;
}
function scaleY(c: number): number {
  return PAD.top + (1 - (c - C_MIN) / (C_MAX - C_MIN)) * PLOT_H;
}

function linearRegression(pts: { x: number; y: number }[]) {
  const n = pts.length;
  const sumX = pts.reduce((s, p) => s + p.x, 0);
  const sumY = pts.reduce((s, p) => s + p.y, 0);
  const sumXY = pts.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = pts.reduce((s, p) => s + p.x * p.x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

const Y_TICKS = [8000, 10000, 12000, 14000, 16000];
const X_TICKS = [8.0, 8.5, 9.0, 9.5, 10.0];

export default function WakeCountyChart({ data, selectedIndices }: WakeCountyChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const { slope, intercept } = useMemo(() => {
    const pts = data.map(d => ({ x: d.quality, y: d.cost }));
    return linearRegression(pts);
  }, [data]);

  const trendX1 = Q_MIN;
  const trendY1 = slope * trendX1 + intercept;
  const trendX2 = Q_MAX;
  const trendY2 = slope * trendX2 + intercept;

  const hoveredCompany = hoveredIdx !== null ? data[hoveredIdx] : null;

  function getDotColor(idx: number, company: { quality: number; value: number }): string {
    if (selectedIndices.includes(idx)) return '#f97316';
    if (company.value > 0.88 && company.quality > 8.6) return '#166534';
    return '#3b82f6';
  }
  function getDotRadius(idx: number): number {
    if (selectedIndices.includes(idx)) return 9;
    if (hoveredIdx === idx) return 8;
    return 5;
  }
  function getDotOpacity(idx: number): number {
    if (selectedIndices.length === 0) return 1;
    if (selectedIndices.includes(idx)) return 1;
    return 0.3;
  }

  return (
    <div className="w-full h-full relative">
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        className="w-full h-full"
        style={{ overflow: 'visible' }}
      >
        {/* Grid lines */}
        {Y_TICKS.map(tick => (
          <line
            key={tick}
            x1={PAD.left}
            x2={PAD.left + PLOT_W}
            y1={scaleY(tick)}
            y2={scaleY(tick)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}
        {X_TICKS.map(tick => (
          <line
            key={tick}
            x1={scaleX(tick)}
            x2={scaleX(tick)}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}

        {/* Axes */}
        <line
          x1={PAD.left} y1={PAD.top}
          x2={PAD.left} y2={PAD.top + PLOT_H}
          stroke="#94a3b8" strokeWidth="1.5"
        />
        <line
          x1={PAD.left} y1={PAD.top + PLOT_H}
          x2={PAD.left + PLOT_W} y2={PAD.top + PLOT_H}
          stroke="#94a3b8" strokeWidth="1.5"
        />

        {/* Y axis labels */}
        {Y_TICKS.map(tick => (
          <text
            key={tick}
            x={PAD.left - 10}
            y={scaleY(tick) + 4}
            textAnchor="end"
            fontSize="11"
            fill="#64748b"
          >
            ${(tick / 1000).toFixed(0)}k
          </text>
        ))}

        {/* X axis labels */}
        {X_TICKS.map(tick => (
          <text
            key={tick}
            x={scaleX(tick)}
            y={PAD.top + PLOT_H + 18}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
          >
            {tick.toFixed(1)}
          </text>
        ))}

        {/* Axis titles */}
        <text
          x={PAD.left + PLOT_W / 2}
          y={VIEWBOX_H - 8}
          textAnchor="middle"
          fontSize="13"
          fontWeight="600"
          fill="#374151"
        >
          Quality Score (0–10)
        </text>
        <text
          x={-(PAD.top + PLOT_H / 2)}
          y={20}
          textAnchor="middle"
          fontSize="13"
          fontWeight="600"
          fill="#374151"
          transform="rotate(-90)"
        >
          Average Cost ($)
        </text>

        {/* Trendline */}
        <line
          x1={scaleX(trendX1)}
          y1={scaleY(trendY1)}
          x2={scaleX(trendX2)}
          y2={scaleY(trendY2)}
          stroke="#94a3b8"
          strokeWidth="1.5"
          strokeDasharray="6 3"
          opacity="0.7"
        />
        <text
          x={scaleX(trendX2) - 4}
          y={scaleY(trendY2) - 8}
          textAnchor="end"
          fontSize="10"
          fill="#94a3b8"
        >
          Market Trend
        </text>

        {/* Legend */}
        <g>
          <circle cx={PAD.left + 14} cy={PAD.top + 14} r={5} fill="#166534" />
          <text x={PAD.left + 24} y={PAD.top + 18} fontSize="10" fill="#166534" fontWeight="600">
            Sweet Spot (Value &gt;0.88 &amp; Quality &gt;8.6)
          </text>
          <circle cx={PAD.left + 225} cy={PAD.top + 14} r={5} fill="#3b82f6" />
          <text x={PAD.left + 235} y={PAD.top + 18} fontSize="10" fill="#3b82f6" fontWeight="600">
            Standard
          </text>
          <circle cx={PAD.left + 308} cy={PAD.top + 14} r={5} fill="#f97316" />
          <text x={PAD.left + 318} y={PAD.top + 18} fontSize="10" fill="#f97316" fontWeight="600">
            Simulated Bid
          </text>
        </g>

        {/* Data points */}
        {data.map((company, idx) => (
          <circle
            key={company.co}
            cx={scaleX(company.quality)}
            cy={scaleY(company.cost)}
            r={getDotRadius(idx)}
            fill={getDotColor(idx, company)}
            opacity={getDotOpacity(idx)}
            style={{ cursor: 'pointer', transition: 'r 0.15s, opacity 0.3s' }}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
          />
        ))}

        {/* Hover tooltip */}
        {hoveredCompany !== null && hoveredIdx !== null && (() => {
          const cx = scaleX(hoveredCompany.quality);
          const cy = scaleY(hoveredCompany.cost);
          const tipX = cx > VIEWBOX_W - 160 ? cx - 145 : cx + 12;
          const tipY = cy < 60 ? cy + 10 : cy - 70;
          const isSweet = hoveredCompany.value > 0.88 && hoveredCompany.quality > 8.6;
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect
                x={tipX}
                y={tipY}
                width={140}
                height={66}
                rx={6}
                fill="white"
                stroke="#e2e8f0"
                strokeWidth="1"
                filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
              />
              <text x={tipX + 10} y={tipY + 18} fontSize="12" fontWeight="700" fill="#1e293b">
                Co #{hoveredCompany.co}
              </text>
              <text x={tipX + 10} y={tipY + 33} fontSize="11" fill="#475569">
                Quality: {hoveredCompany.quality.toFixed(1)}
              </text>
              <text x={tipX + 10} y={tipY + 47} fontSize="11" fill="#475569">
                Cost: ${hoveredCompany.cost.toLocaleString()}
              </text>
              <text x={tipX + 10} y={tipY + 61} fontSize="11" fill={isSweet ? '#166534' : '#64748b'} fontWeight="600">
                Value: {hoveredCompany.value.toFixed(2)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
